import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { useAppUIContext, AppModule } from '../../context/AppUIContext';
import { useResponsive } from '../../hooks/useResponsive';
import LeftMenu from './LeftMenu';
import WorkspaceSitemapModal from './WorkspaceSitemapModal';
import Page from '../../const/Page';
import BoardProvider from '../board/BoardProvider';
import CalendarProvider from '../calendar/CalendarProvider';

/** URL 경로에서 현재 활성 모듈을 판별 */
function getModuleFromPath(pathname: string): AppModule {
    if (pathname.startsWith(Page.board)) return 'board';
    if (pathname.startsWith(Page.calendar)) return 'calendar';
    if (pathname.startsWith(Page.files)) return 'files';
    return 'chat';
}

interface AppLayoutProps {
    children: ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    const { isMobileMenuOpen, setIsMobileMenuOpen, activeModule, setActiveModule } = useAppUIContext();
    const { isSidebarCollapsed } = useResponsive();
    const location = useLocation();
    const [sitemapOpen, setSitemapOpen] = React.useState(false);
    const [sitemapFocusInput, setSitemapFocusInput] = React.useState(false);

    useEffect(() => {
        const handleOpenSitemap = (e: Event) => {
            setSitemapOpen(true);
            const isShortcut = (e as CustomEvent).detail?.focusInput === true;
            setSitemapFocusInput(isShortcut);
        };
        window.addEventListener('open-workspace-sitemap', handleOpenSitemap);
        return () => window.removeEventListener('open-workspace-sitemap', handleOpenSitemap);
    }, []);

    const path = location.pathname;
    const isSidebarHidden = (
        path === Page.publicSharePage || path.startsWith(Page.publicSharePage + '/') ||
        path === Page.publicBotPage || path.startsWith(Page.publicBotPage + '/')
    );

    // Close mobile menu on route change & sync activeModule from URL
    useEffect(() => {
        setIsMobileMenuOpen(false);
        const detectedModule = getModuleFromPath(location.pathname);
        if (detectedModule !== activeModule) {
            setActiveModule(detectedModule);
        }
    }, [location.pathname, setIsMobileMenuOpen]);

    return (
        <BoardProvider>
         <CalendarProvider>
            <div className={`flex flex-col h-screen w-full overflow-hidden ${isSidebarCollapsed ? 'bg-background' : 'bg-muted p-2 gap-2'}`}>
                {/* Desktop: Sidebar(AppRail 통합) + Main */}
                <div className="flex flex-1 min-h-0 gap-inherit">
                    {/* Desktop Sidebar — 헤더(프로필+워크스페이스) / 본문(모듈 스위처 | 콘텐츠) / 푸터(NEXTPOD) */}
                    {!isSidebarCollapsed && !isSidebarHidden && (
                        <aside className="w-[320px] shrink-0 h-full overflow-hidden">
                            <LeftMenu />
                        </aside>
                    )}

                    {/* Mobile Sidebar — Sheet으로 표시 */}
                    {isSidebarCollapsed && (
                        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                            {/* 폭: %+max-width — 우측 backdrop 탭 여백 확보 (390px 뷰포트 기준 ~78px)
                            QA 배지 클릭 시 닫힘 예외는 sheet.tsx 프리미티브 공통 처리 */}
                        <SheetContent side="left" className="p-0 w-4/5 max-w-[340px] border-r-0" hideClose>
                                <LeftMenu />
                            </SheetContent>
                        </Sheet>
                    )}

                    {/* Main Content - 자식 컴포넌트가 직접 패널 스타일 관리 */}
                    <main className="flex-1 h-full overflow-hidden relative">
                        {children}
                    </main>
                </div>

                {/* 모바일 하단 탭바 폐기 — 모듈 전환은 좌측 메뉴(Sheet)의 Module Rail 로 통일 (채널 헤더 탭과 혼동 방지) */}
                <WorkspaceSitemapModal open={sitemapOpen} onOpenChange={setSitemapOpen} autoFocusInput={sitemapFocusInput} />
            </div>
         </CalendarProvider>
        </BoardProvider>
    );
};

